import { gql } from "graphql-tag";

type PropertyContractsSidebarQueryResult<T extends { id: string; status?: string | null }> = {
  propertyContractsSidebar: T[];
};

type SubscriptionContractPayload<T> = {
  data?: { propertyContractSidebarUpdated?: T | null } | null;
};

/** Server only publishes sent, pending, signed, and cancelled contracts. */
export const PROPERTY_CONTRACT_SIDEBAR_UPDATED_SUBSCRIPTION = gql`
  subscription PropertyContractSidebarUpdated {
    propertyContractSidebarUpdated {
      id
      propertyName
      status
      interestListId
    }
  }
`;

/** Merges sidebar subscription payloads into `propertyContractsSidebar` cache. */
export const mergePropertyContractSidebarUpdatedIntoList = <
  T extends { id: string; status?: string | null; interestListId?: string | null },
>(
  prev: PropertyContractsSidebarQueryResult<T>,
  subscriptionData: unknown,
): PropertyContractsSidebarQueryResult<T> => {
  const payload = subscriptionData as SubscriptionContractPayload<T> | undefined;
  const next = payload?.data?.propertyContractSidebarUpdated;
  if (!next?.id) {
    return prev;
  }

  const status = String(next.status || "").toLowerCase();
  const keepOnSidebar =
  [
    "sent",
    "pending",
  ].includes(status) ||
    (status === "signed" && Boolean(next.interestListId));

  if (!keepOnSidebar) {
    return {
      ...prev,
      propertyContractsSidebar: prev.propertyContractsSidebar.filter((c) => c.id !== next.id),
    };
  }

  const idx = prev.propertyContractsSidebar.findIndex((c) => c.id === next.id);
  if (idx === -1) {
    return {
      ...prev,
      propertyContractsSidebar: [next, ...prev.propertyContractsSidebar],
    };
  }
  const merged = prev.propertyContractsSidebar.map((c) => (
    c.id === next.id ? { ...c, ...next } : c
  ));
  return { ...prev, propertyContractsSidebar: merged };
};
